import React from 'react';
import { Skeleton, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FormattedMessage } from 'react-intl';
import { TranslationKeys } from '../../../../../translations';
import type { CardState } from '../types';
import {
  InfoCardStyled,
  CardHeaderBandStyled,
  CardIconCircleStyled,
  CardBodyStyled,
  FieldListStyled,
  PlaceholderStateStyled,
  NotFoundStateStyled,
  SkeletonBlockStyled,
} from '../ConnectionInspectorPage.styles';

type InfoCardProps = {
  icon: IconDefinition;
  color: string;
  titleId: string;
  placeholderTextId: string;
  state: CardState;
  children?: React.ReactNode;
};

export const InfoCard = ({ icon, color, titleId, placeholderTextId, state, children }: InfoCardProps) => (
  <InfoCardStyled elevation={0}>
    <CardHeaderBandStyled bandcolor={color}>
      <CardIconCircleStyled bandcolor={color}>
        <FontAwesomeIcon icon={icon} />
      </CardIconCircleStyled>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: '0.72rem',
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color,
          lineHeight: 1.2,
        }}
      >
        <FormattedMessage id={titleId} />
      </Typography>
    </CardHeaderBandStyled>

    <CardBodyStyled>
      {state === 'idle' && (
        <PlaceholderStateStyled>
          <FontAwesomeIcon icon={faMagnifyingGlass} style={{ fontSize: '1.6rem' }} />
          <span>
            <FormattedMessage id={placeholderTextId} />
          </span>
        </PlaceholderStateStyled>
      )}

      {state === 'loading' && (
        <SkeletonBlockStyled>
          {[0, 1, 2, 3].map(i => (
            <React.Fragment key={`skeleton-${i}`}>
              <Skeleton width="40%" height={11} sx={{ mb: 0.25 }} />
              <Skeleton width="70%" height={18} sx={{ mb: 1.5 }} />
            </React.Fragment>
          ))}
        </SkeletonBlockStyled>
      )}

      {state === 'not-found' && (
        <NotFoundStateStyled>
          <FontAwesomeIcon icon={faCircleExclamation} style={{ fontSize: '1.6rem' }} />
          <span>
            <FormattedMessage id={TranslationKeys.tools_connectionInspector_notFound} />
          </span>
        </NotFoundStateStyled>
      )}

      {state === 'found' && <FieldListStyled>{children}</FieldListStyled>}
    </CardBodyStyled>
  </InfoCardStyled>
);
